// Performance V2 — Audience tab. Sub-views: Geography (#5922 — choropleth +
// top-locations table), Device (#5922 — per-device metrics), Demographics
// (#4298 — age / gender / parental status with bid adjustments). Extracted
// from performance-v2.tsx (#4932 Phase 0.3). The ICP Segments placeholder was
// dropped 09-09: nothing routed to it once the consolidated panel took over
// the sub-view switcher.
// The sub-view selector renders in the shell's header toolbar, so the active
// sub-view arrives as a prop; each sub-view's query is lazy on that sub-view
// (the module mounts only when the tab is active).
import { Spinner } from '@/components/glitch-ui/spinner';
import { useMemo, useState } from 'react';
import { useQueries } from '@tanstack/react-query';
import { MapPin } from 'lucide-react';
import { ColumnDef } from '@tanstack/react-table';
import { GeographyChoropleth } from './geography-choropleth';

import type { DemographicDimension, DemographicRow, GeographyRow, DeviceRow } from '@/lib/apis';
import type { CampaignData } from '@/lib/types';
import {
  useAccountDemographicsReportQuery,
  useAccountGeographyReportQuery,
  useAccountDeviceReportQuery,
  bidModifiersQueryOptions,
} from '@/lib/query-options';
import { Card } from '@/components/ui/card';
import { DataTable } from '@/components/glitch-ui/data-table';
import { cn } from '@/lib/utils';
import { explainDemographics } from '../explainers';
import { fmt, KeywordStatusBadge, MutedDash } from '../cells';

export type AudienceSubView = 'geography' | 'device' | 'demographics';

/** Report row plus the never-live seeding flag: a targeted location with no
    traffic yet renders dashes, never zeros (ruling 09-09). */
type GeoRow = GeographyRow & { unmeasured?: boolean };

/** Device row plus the seeding flag and the campaign's own bid adjustment —
    a decision already made, worth showing before any traffic exists. */
type DevRow = DeviceRow & { unmeasured?: boolean; bidModifier?: number | null };

// Google segments every impression by device, so the three types always exist
// — no targeting needed. Seeded rows use Google's own labels.
export const DEMOGRAPHIC_DIMENSIONS: { value: DemographicDimension; label: string }[] = [
  { value: 'age', label: 'Age' },
  { value: 'gender', label: 'Gender' },
  { value: 'parental_status', label: 'Parental status' },
];

const SEEDED_DEVICES: { key: string; label: string }[] = [
  { key: 'MOBILE', label: 'Mobile devices with full browsers' },
  { key: 'DESKTOP', label: 'Computers' },
  { key: 'TABLET', label: 'Tablets with full browsers' },
];

export function AudienceTab({
  subView,
  campaignIds,
  currencySymbol,
  dateRangeFilter,
  campaigns,
  reportsEnabled = true,
  demographicDimension: dimensionProp,
}: {
  subView: AudienceSubView;
  campaignIds: string | undefined;
  currencySymbol: string;
  dateRangeFilter: { start_date: string; end_date: string };
  /** Never-live seeding (ruling 09-09): a draft campaign's TARGET locations
      list on Geography with dashed metrics, so the tab shows the geo
      configuration before any traffic exists. */
  campaigns?: CampaignData[];
  /** False when no campaign has a Google Ads id: the account reports would
      otherwise run account-wide (#6275), so they stay disabled and only the
      seeds render. */
  reportsEnabled?: boolean;
  /** Controlled by the panel so its switcher can sit on the sub-view row
      (ruling 09-09); falls back to local state when absent. */
  demographicDimension?: DemographicDimension;
}) {
  const cs = currencySymbol;

  // #4298 — Demographics dimension picker inside the Audience › Demographics
  // sub-view. The backend keys queries by dimension, so switching the picker
  // refetches (cached per dimension by React Query).
  const [ownDimension] = useState<DemographicDimension>('age');
  const demographicDimension = dimensionProp ?? ownDimension;

  const { data: demographicsData, isLoading: isDemographicsLoading } = useAccountDemographicsReportQuery(
    demographicDimension,
    dateRangeFilter.start_date,
    dateRangeFilter.end_date,
    campaignIds || undefined,
    { enabled: reportsEnabled && subView === 'demographics' },
  );

  // #5922 — geography + device, each lazy on its own sub-view.
  // Account-level reports (#6051): campaignIds is an optional filter, not a requirement. Gating the
  // query on it left the Audience tab empty for accounts that have a connected Google Ads account with
  // data but no glitch-managed campaigns (so the derived campaignIds is blank). Run account-wide when
  // campaignIds is absent, scoped when it's present — consistent with the Campaigns/Creative tabs.
  const { data: geographyData, isLoading: isGeographyLoading } = useAccountGeographyReportQuery(
    dateRangeFilter.start_date,
    dateRangeFilter.end_date,
    campaignIds || undefined,
    { enabled: reportsEnabled && subView === 'geography' },
  );
  const { data: deviceData, isLoading: isDeviceLoading } = useAccountDeviceReportQuery(
    dateRangeFilter.start_date,
    dateRangeFilter.end_date,
    campaignIds || undefined,
    { enabled: reportsEnabled && subView === 'device' },
  );

  // Never-live campaigns' TARGET locations, deduped, as dashed rows: the geo
  // configuration is a real local fact worth showing before traffic exists
  // (ruling 09-09). Report rows always win — these only render when the
  // report has none.
  // Device bid adjustments for the never-live campaigns (per-campaign, so a
  // fan-out; cached per campaign id, and only fetched on the Device sub-view).
  const neverLiveCampaigns = useMemo(
    () => (campaigns ?? []).filter((c) => !c.googleads_campaign?.googleads_campaign_id || c.state === 'draft'),
    [campaigns],
  );
  const bidModifierQueries = useQueries({
    queries: neverLiveCampaigns.map((c) => ({
      ...bidModifiersQueryOptions(c.id),
      enabled: subView === 'device' && !!c.id,
    })),
  });
  const seededDeviceRows = useMemo<DevRow[]>(() => {
    if (neverLiveCampaigns.length === 0) return [];
    // One adjustment per device across the scoped campaigns: the last one wins
    // rather than averaging values that belong to different campaigns.
    const modifierByDevice = new Map<string, number>();
    for (const q of bidModifierQueries) {
      for (const d of q.data?.device ?? []) {
        if (d.device_type) modifierByDevice.set(String(d.device_type).toUpperCase(), d.modifier_value);
      }
    }
    return SEEDED_DEVICES.map(({ key, label }) => ({
      device: label,
      impressions: 0, clicks: 0, cost: 0, conversions: 0, conversion_value: 0,
      ctr: 0, avg_cpc: 0, conv_rate: 0, cost_per_conv: 0,
      unmeasured: true,
      bidModifier: modifierByDevice.get(key) ?? null,
    }));
  }, [neverLiveCampaigns, ...bidModifierQueries.map((q) => q.data)]);

  const seededGeographyRows = useMemo<GeoRow[]>(() => {
    const byId = new Map<number, GeoRow>();
    for (const c of neverLiveCampaigns) {
      for (const loc of c.target_locations ?? []) {
        if (byId.has(loc.criteria_id)) continue;
        byId.set(loc.criteria_id, {
          country: loc.canonical_name || loc.name,
          criterion_id: String(loc.criteria_id),
          impressions: 0, clicks: 0, cost: 0, conversions: 0, conversion_value: 0,
          ctr: 0, avg_cpc: 0, conv_rate: 0, cost_per_conv: 0,
          unmeasured: true,
        });
      }
    }
    return [...byId.values()];
  }, [neverLiveCampaigns]);

  const geographyColumns: ColumnDef<GeoRow, unknown>[] = [
    // "Location", not "Country": target locations can be regions or cities,
    // and a header that changes with row provenance is worse than a broader
    // one that is true of both.
    { accessorKey: 'country', header: 'Location', cell: ({ row }) => (
      <span className="text-sm font-medium text-gray-900">{row.original.country}</span>
    )},
    { accessorKey: 'impressions', header: 'Imp.', cell: ({ row }) => row.original.unmeasured ? <MutedDash /> : row.original.impressions.toLocaleString() },
    { accessorKey: 'clicks', header: 'Clicks', cell: ({ row }) => row.original.unmeasured ? <MutedDash /> : row.original.clicks.toLocaleString() },
    { accessorKey: 'conversions', header: 'Conv.', cell: ({ row }) => row.original.unmeasured ? <MutedDash /> : row.original.conversions.toFixed(0) },
    { accessorKey: 'ctr', header: 'CTR', cell: ({ row }) => row.original.unmeasured ? <MutedDash /> : `${(row.original.ctr * 100).toFixed(2)}%` },
    { accessorKey: 'avg_cpc', header: 'Avg. CPC', cell: ({ row }) => row.original.unmeasured ? <MutedDash /> : fmt(row.original.avg_cpc, cs) },
    { accessorKey: 'cost', header: 'Cost', cell: ({ row }) => row.original.unmeasured ? <MutedDash /> : fmt(row.original.cost, cs) },
    { accessorKey: 'conv_rate', header: 'Conv. rate', cell: ({ row }) => row.original.unmeasured ? <MutedDash /> : `${(row.original.conv_rate * 100).toFixed(1)}%` },
    { accessorKey: 'cost_per_conv', header: 'Cost/conv', cell: ({ row }) => row.original.unmeasured || row.original.conversions === 0 ? <MutedDash /> : fmt(row.original.cost_per_conv, cs) },
  ];

  return (
    <>
      {subView === 'geography' && (() => {
        const reported = geographyData ?? [];
        const rows: GeoRow[] = reported.length > 0 ? reported : seededGeographyRows;
        const hasRows = rows.length > 0;
        const seeded = reported.length === 0 && rows.length > 0;
        if (isGeographyLoading && !hasRows) {
          return (
            <div className="flex items-center justify-center py-16">
              <Spinner size="lg" className="text-gray-400" />
              <span className="ml-3 text-sm text-gray-500">Loading geographic performance…</span>
            </div>
          );
        }
        if (!hasRows) {
          // Populated-shape empty state (09-09): the real two-card layout
          // with ghost content, and the table below in its standard empty
          // state — same silhouette as a populated window.
          return (
            <div className="grid grid-cols-12 gap-4">
              <Card className="col-span-7 p-4 border border-gray-200 shadow-sm">
                <h4 className="text-sm font-semibold text-gray-900 mb-3">Geographic Performance</h4>
                <div className="flex h-[240px] flex-col items-center justify-center rounded-lg bg-gray-50 px-6 text-center">
                  <MapPin className="mb-3 h-10 w-10 text-gray-300" strokeWidth={1} />
                  <p className="text-sm text-gray-400">This map highlights where your ads perform best, once campaigns have impressions in the selected period.</p>
                </div>
              </Card>
              <Card className="col-span-5 p-4 border border-gray-200 shadow-sm">
                <h4 className="text-sm font-semibold text-gray-900 mb-3">Top Locations</h4>
                <div aria-hidden className="space-y-3 pt-1">
                  {[0, 1, 2, 3, 4, 5].map((i) => (
                    <div key={i}>
                      <div className="mb-1 flex items-center justify-between">
                        <div className="h-2 w-24 rounded bg-gray-100" />
                        <div className="h-2 w-16 rounded bg-gray-100" />
                      </div>
                      <div className="h-1 rounded bg-gray-100" />
                    </div>
                  ))}
                </div>
              </Card>
              <div className="col-span-12">
                <DataTable<GeographyRow>
                  data={[]}
                  columns={geographyColumns}
                  pageSize={15}
                  variant="bordered"
                  emptyState="No geographic performance data available for this period"
                />
              </div>
            </div>
          );
        }
        const maxConversions = rows.reduce((m, r) => Math.max(m, r.conversions), 0);
        const topLocations = rows.slice(0, 8);
        return (
          <div className="grid grid-cols-12 gap-4">
            <Card className="col-span-7 p-4 border border-gray-200 shadow-sm">
              <h4 className="text-sm font-semibold text-gray-900 mb-3">Geographic Performance</h4>
              {seeded ? (
                // Seeded rows are TARGETING, not traffic: shading a map by
                // zero would read as measured nothing.
                <div className="flex h-[240px] flex-col items-center justify-center rounded-lg bg-gray-50 px-6 text-center">
                  <MapPin className="mb-3 h-10 w-10 text-gray-300" strokeWidth={1} />
                  <p className="text-sm text-gray-400">This map highlights where your ads perform best, once campaigns go live and start getting impressions.</p>
                </div>
              ) : (
                <>
                  <GeographyChoropleth rows={rows} cs={cs} />
                  <p className="text-[11px] text-gray-400 mt-2">Darker countries performed better on the selected metric. Hover for details; exact figures in the table.</p>
                </>
              )}
            </Card>

            <Card className="col-span-5 p-4 border border-gray-200 shadow-sm">
              <h4 className="text-sm font-semibold text-gray-900 mb-3">{seeded ? 'Targeted Locations' : 'Top Locations'}</h4>
              <div className="space-y-2.5">
                {topLocations.map((r) => (
                  <div key={r.criterion_id}>
                    <div className="flex items-center justify-between text-sm">
                      <span className="text-gray-900 truncate max-w-[160px]">{r.country}</span>
                      <span className="text-gray-500 text-xs">
                        {r.unmeasured ? <MutedDash /> : `${r.conversions.toFixed(0)} conv · ${fmt(r.cost, cs)}`}
                      </span>
                    </div>
                    <div className="h-1 rounded bg-gray-100 overflow-hidden mt-1">
                      <div
                        className="h-full bg-[#290BD9]"
                        style={{ width: `${maxConversions > 0 ? Math.max((r.conversions / maxConversions) * 100, 2) : 0}%` }}
                      />
                    </div>
                  </div>
                ))}
              </div>
            </Card>

            <div className="col-span-12 space-y-2">
              <DataTable<GeoRow>
                data={rows}
                columns={geographyColumns}
                pageSize={15}
                variant="bordered"
                emptyState="No geographic performance data available"
              />
              {seeded && (
                <p className="px-1 text-xs text-gray-400">
                  Locations these campaigns target. Performance fills in once they go live.
                </p>
              )}
            </div>
          </div>
        );
      })()}

      {subView === 'device' && (() => {
        const reported = deviceData ?? [];
        const rows: DevRow[] = reported.length > 0 ? reported : seededDeviceRows;
        const hasRows = rows.length > 0;
        const seeded = reported.length === 0 && rows.length > 0;
        if (isDeviceLoading && !hasRows) {
          return (
            <div className="flex items-center justify-center py-16">
              <Spinner size="lg" className="text-gray-400" />
              <span className="ml-3 text-sm text-gray-500">Loading device performance…</span>
            </div>
          );
        }
        // Empty windows fall through to the table's standard empty state
        // (09-09) — the same silhouette as every other table on the surface.
        const totalConversions = rows.reduce((s, r) => s + r.conversions, 0);
        const deviceColumns: ColumnDef<DevRow, unknown>[] = [
          { accessorKey: 'device', header: 'Device', cell: ({ row }) => (
            <div className="w-40">
              <div className="text-sm font-medium text-gray-900">{row.original.device}</div>
              {/* Share of total conversions across devices. */}
              <div className="h-1 rounded bg-gray-100 overflow-hidden mt-1">
                <div
                  className="h-full bg-[#290BD9]"
                  style={{ width: `${totalConversions > 0 ? Math.max((row.original.conversions / totalConversions) * 100, 2) : 0}%` }}
                />
              </div>
            </div>
          )},
          // The campaign's own device bid adjustment: a decision already made,
          // so it shows as a real value even before any traffic (ruling 09-09).
          { id: 'bidModifier', header: 'Bid adj.', cell: ({ row }) => {
            const bm = row.original.bidModifier;
            if (bm == null) return <MutedDash />;
            const pct = bm * 100;
            const cls = pct > 0 ? 'text-green-700' : pct < 0 ? 'text-red-700' : 'text-gray-500';
            return <span className={cn('text-sm', cls)}>{pct === 0 ? '0%' : `${pct > 0 ? '+' : ''}${pct.toFixed(0)}%`}</span>;
          }},
          { accessorKey: 'impressions', header: 'Imp.', cell: ({ row }) => row.original.unmeasured ? <MutedDash /> : row.original.impressions.toLocaleString() },
          { accessorKey: 'clicks', header: 'Clicks', cell: ({ row }) => row.original.unmeasured ? <MutedDash /> : row.original.clicks.toLocaleString() },
          { accessorKey: 'conversions', header: 'Conv.', cell: ({ row }) => row.original.unmeasured ? <MutedDash /> : row.original.conversions.toFixed(0) },
          { accessorKey: 'ctr', header: 'CTR', cell: ({ row }) => row.original.unmeasured ? <MutedDash /> : `${(row.original.ctr * 100).toFixed(2)}%` },
          { accessorKey: 'avg_cpc', header: 'Avg. CPC', cell: ({ row }) => row.original.unmeasured ? <MutedDash /> : fmt(row.original.avg_cpc, cs) },
          { accessorKey: 'cost', header: 'Cost', cell: ({ row }) => row.original.unmeasured ? <MutedDash /> : fmt(row.original.cost, cs) },
          { accessorKey: 'conv_rate', header: 'Conv. rate', cell: ({ row }) => row.original.unmeasured ? <MutedDash /> : `${(row.original.conv_rate * 100).toFixed(1)}%` },
          { accessorKey: 'cost_per_conv', header: 'Cost/conv', cell: ({ row }) => row.original.unmeasured || row.original.conversions === 0 ? <MutedDash /> : fmt(row.original.cost_per_conv, cs) },
        ];
        return (
          <div className="space-y-2">
            <DataTable<DevRow>
              data={rows}
              columns={deviceColumns}
              pageSize={10}
              variant="bordered"
              emptyState="No device performance data available for this period"
            />
            {seeded && (
              <p className="px-1 text-xs text-gray-400">
                Every campaign serves on all three device types. Performance fills in once they go live.
              </p>
            )}
          </div>
        );
      })()}

      {/* #4298 — Demographics sub-view. */}
      {subView === 'demographics' && (() => {
        const hasRows = Array.isArray(demographicsData) && demographicsData.length > 0;
        // Pretty-print Google Ads enum values. "AGE_RANGE_35_44" → "35-44",
        // "PARENT" → "Parent", "MALE" → "Male", "UNDETERMINED" → "Undetermined".
        const formatSegment = (seg: string | null): string => {
          if (!seg) return '—';
          const m = seg.match(/^AGE_RANGE_(\d+)_(\d+)$/);
          if (m) return `${m[1]}-${m[2]}`;
          if (seg === 'AGE_RANGE_65_UP') return '65+';
          if (seg === 'AGE_RANGE_UNDETERMINED' || seg === 'UNDETERMINED') return 'Unknown';
          if (seg === 'NOT_A_PARENT') return 'Not a parent';
          return seg.charAt(0) + seg.slice(1).toLowerCase().replace(/_/g, ' ');
        };
        const demographicColumns: ColumnDef<DemographicRow, unknown>[] = [
          { accessorKey: 'segment', header: ({ table: _t }) => {
            if (demographicDimension === 'age') return 'Age';
            if (demographicDimension === 'gender') return 'Gender';
            return 'Parental status';
          }, cell: ({ row }) => (
            <div>
              <div className="text-sm font-medium text-gray-900">{formatSegment(row.original.segment)}</div>
              <div className="text-xs text-gray-500 mt-0.5 flex items-center gap-1.5">
                <KeywordStatusBadge status={row.original.status} />
                <span className="truncate max-w-[160px]">{row.original.campaign_name}</span>
                <span className="text-gray-400">·</span>
                <span className="truncate max-w-[160px]">{row.original.ad_group_name}</span>
              </div>
            </div>
          )},
          { accessorKey: 'bid_modifier', header: 'Bid adj.', cell: ({ row }) => {
            const bm = row.original.bid_modifier;
            if (bm == null) return <MutedDash />;
            const pct = (bm - 1) * 100;
            const cls = pct > 0 ? 'text-green-700' : pct < 0 ? 'text-red-700' : 'text-gray-500';
            return <span className={cn('text-sm', cls)}>{pct === 0 ? '0%' : `${pct > 0 ? '+' : ''}${pct.toFixed(0)}%`}</span>;
          }},
          { accessorKey: 'impressions', header: 'Imp.', cell: ({ row }) => row.original.impressions.toLocaleString() },
          { accessorKey: 'clicks', header: 'Clicks', cell: ({ row }) => row.original.clicks.toLocaleString() },
          { accessorKey: 'conversions', header: 'Conv.', cell: ({ row }) => row.original.conversions.toFixed(0) },
          { accessorKey: 'ctr', header: 'CTR', cell: ({ row }) => `${(row.original.ctr * 100).toFixed(2)}%` },
          { accessorKey: 'avg_cpc', header: 'Avg. CPC', cell: ({ row }) => fmt(row.original.avg_cpc, cs) },
          { accessorKey: 'cost', header: 'Cost', cell: ({ row }) => fmt(row.original.cost, cs) },
          { accessorKey: 'conv_rate', header: 'Conv. rate', cell: ({ row }) => `${(row.original.conv_rate * 100).toFixed(1)}%` },
          { accessorKey: 'cost_per_conv', header: 'Cost/conv', cell: ({ row }) => row.original.conversions > 0 ? fmt(row.original.cost_per_conv, cs) : <MutedDash /> },
        ];
        return (
          <div className="space-y-3">
            {isDemographicsLoading && !hasRows ? (
              <div className="flex items-center justify-center py-16">
                <Spinner size="lg" className="text-gray-400" />
                <span className="ml-3 text-sm text-gray-500">Loading demographics…</span>
              </div>
            ) : (
              // Empty windows keep the dimension picker + the table's
              // standard empty state (09-09) — the populated silhouette.
              <>
                <DataTable<DemographicRow>
                  data={(demographicsData as DemographicRow[] | undefined) ?? []}
                  columns={demographicColumns}
                  pageSize={20}
                  variant="bordered"
                  emptyState="No demographic performance data available for this period"
                />
                {!hasRows && (
                  // Unlike locations and devices, demographics is not something
                  // you configure — Google infers it from who clicks. Saying so
                  // beats an unexplained empty table (ruling 09-09).
                  <p className="px-1 text-xs text-gray-400">
                    Google infers age, gender and parental status from the people who see and click your ads.
                    Rows appear once campaigns serve; no setup is needed.
                  </p>
                )}
                {/* #4306 — auto-explainer footnote. */}
                {(() => {
                  if (!hasRows) return null;
                  const narrative = explainDemographics(demographicsData as DemographicRow[], demographicDimension);
                  return narrative ? <p className="text-xs text-gray-400 mt-2 px-1">{narrative}</p> : null;
                })()}
              </>
            )}
          </div>
        );
      })()}
    </>
  );
}
